import React from 'react';
import { Button, Collapse, Empty, Popconfirm } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import ImageFx from '../../../commonComponents/ImageFx';
import { useSkuActivity } from '../../../hooks/useStudioOps';
import HeroGarment, { ModelImage } from './HeroGarment';
import SkuActivityList from '../SkuActivity';
import { flagText, productionStages } from './stages';
import styles from './modal.module.css';

function StageRow({ sku }) {
    const stages = productionStages(sku);
    return (
        <div className={styles.stages}>
            {stages.map((stage, index) => (
                <div className={styles.stage} key={stage.key}>
                    <span
                        className={`${styles.dot} ${stage.state === 'done' ? styles.dotDone : ''} ${stage.state === 'pending' ? styles.dotPending : ''}`}
                    >
                        {stage.state === 'done' ? <CheckOutlined style={{ fontSize: 12 }} /> : stage.state === 'pending' ? '•' : ''}
                    </span>
                    <span>
                        <strong>{stage.label}</strong>
                        {stage.sub ? <small>{stage.sub}</small> : null}
                    </span>
                    {index < stages.length - 1 ? <span className={styles.stageLine} /> : null}
                </div>
            ))}
        </div>
    );
}

function FlagLine({ label, value }) {
    const text = flagText(value);
    return (
        <>
            <span className={styles.flagLabel}>{label}</span>
            <span className={text === 'Yes' ? styles.flagYes : text === 'No' ? styles.flagNo : undefined}>{text}</span>
        </>
    );
}

export default function OverviewTab({ sku, files, onOpenTab, onCCRequired }) {
    const { data: activity } = useSkuActivity(sku?._id);
    const latestComment = [...(sku?.rawQCFilesCommentsMerged || [])].sort((a, b) => new Date(b.at) - new Date(a.at))[0];
    const reopened = (activity || []).find(row => /reopen/i.test(row.action || ''));
    const showAlert = !!latestComment || !!reopened;
    const filesSorted = [...(files || [])].sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0));
    const pairing = [...(sku?.supportingBatchArticles || []), ...(sku?.supportingArticles || [])];
    const angles = sku?.angles || [];

    return (
        <div>
            {showAlert ? (
                <div className={styles.alert}>
                    <div>
                        <strong>{reopened ? reopened.action : 'Latest Raw QC comment'}</strong>
                        <p>{latestComment?.comment || reopened?.detail || 'No comment text recorded'}</p>
                    </div>
                    <button type="button" className={styles.linkButton} onClick={() => onOpenTab('activity')}>
                        View activity
                    </button>
                </div>
            ) : null}
            <StageRow sku={sku} />
            <div className={styles.grid}>
                <div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>Product & reference</h3>
                        <div className={styles.productRow}>
                            <HeroGarment sku={sku} />
                            <ModelImage sku={sku} />
                        </div>
                    </div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>
                            Angle preset
                            <button type="button" className={styles.linkButton} onClick={() => onOpenTab('images')}>
                                View all
                            </button>
                        </h3>
                        <p style={{ margin: '0 0 12px' }}>{sku?.anglePresetAlias || 'No angle preset'}</p>
                        <div className={styles.fileStrip}>
                            {angles.length ? (
                                angles.map((angle, index) => (
                                    <ImageFx
                                        key={angle?.angle?._id || angle?.angle?.name || index}
                                        image={angle?.angle?.file}
                                        allowFullSize
                                        header={angle?.angle?.name}
                                        caption={angle?.angle?.shootType}
                                        showVerifiedIcon={!!angle?.file}
                                        shootStatus={sku?.shootStatus}
                                        width="120px"
                                        height="150px"
                                    />
                                ))
                            ) : (
                                <Empty description="No angles on this preset" />
                            )}
                        </div>
                    </div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>
                            Studio files
                            <button type="button" className={styles.linkButton} onClick={() => onOpenTab('images')}>
                                View all
                            </button>
                        </h3>
                        <div className={styles.fileStrip}>
                            {filesSorted.length ? (
                                filesSorted.slice(0, 4).map(file => (
                                    <ImageFx
                                        key={file?._id || file?.createdAt}
                                        image={file?.image}
                                        allowFullSize
                                        usageCount={file?.usageCount}
                                        isStudioCCFile={file?.isStudioCCFile}
                                        header="Studio file"
                                        caption={file?.createdAt ? new Date(file.createdAt).toLocaleString() : ''}
                                        width="120px"
                                        height="150px"
                                    />
                                ))
                            ) : (
                                <Empty />
                            )}
                        </div>
                    </div>
                    <Collapse
                        items={[
                            {
                                key: 'pairing',
                                label: `Supporting / pairing articles (${pairing.length})`,
                                children: pairing.length ? (
                                    <div className={styles.fileStrip}>
                                        {pairing.map((article, index) => (
                                            <ImageFx
                                                key={article?._id || index}
                                                image={article.image}
                                                allowFullSize
                                                caption={article?.type}
                                                header="Pairing article"
                                                width="120px"
                                                height="150px"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <Empty description="No supporting / pairing articles linked." />
                                ),
                            },
                        ]}
                    />
                </div>
                <div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>
                            SKU summary
                            <button type="button" className={styles.linkButton} onClick={() => onOpenTab('client')}>
                                View client data
                            </button>
                        </h3>
                        <div className={styles.flags}>
                            <span className={styles.flagLabel}>EAN</span>
                            <span>{sku?.csvData?.EAN || sku?.csvData?.barcode || sku?.skuName || '—'}</span>
                            <span className={styles.flagLabel}>Order</span>
                            <span>{sku?.article?.orderName || '—'}</span>
                            <span className={styles.flagLabel}>Barcodes</span>
                            <span>{(sku?.barcodes || []).join(', ') || '—'}</span>
                        </div>
                    </div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>
                            Latest activity
                            <button type="button" className={styles.linkButton} onClick={() => onOpenTab('activity')}>
                                View full history
                            </button>
                        </h3>
                        <SkuActivityList skuId={sku?._id} preview={4} />
                    </div>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>Production flags</h3>
                        <div className={styles.flags}>
                            <FlagLine label="DD Approved" value={sku?.isDDApproved} />
                            <FlagLine label="Completed At Raw QC" value={sku?.isRawQCComplete} />
                            <FlagLine label="Shoot Complete" value={!!(sku?.isPendingAtRawQC || sku?.isRawQCComplete)} />
                            <FlagLine label="Pending At RawQC" value={sku?.isPendingAtRawQC} />
                            <span className={styles.flagLabel}>Is CC Required</span>
                            {sku?.isCCRequired == null && sku?.isRawQCComplete != false ? (
                                <Popconfirm
                                    title="Modify CC Required"
                                    okText="CC is Not Required"
                                    cancelText="CC is Required"
                                    okButtonProps={{ style: { background: '#f85479', borderColor: '#f85479' } }}
                                    cancelButtonProps={{ style: { color: '#c4234d', borderColor: '#f5b8c6' } }}
                                    onConfirm={() => onCCRequired(false)}
                                    onCancel={() => onCCRequired(true)}
                                >
                                    <Button size="small" type="primary">Set CC required</Button>
                                </Popconfirm>
                            ) : (
                                <span className={flagText(sku?.isCCRequired) === 'Yes' ? styles.flagYes : flagText(sku?.isCCRequired) === 'No' ? styles.flagNo : undefined}>
                                    {flagText(sku?.isCCRequired)}
                                </span>
                            )}
                            <FlagLine label="Is CC Uploaded" value={sku?.isCCUploaded} />
                            <FlagLine label="Marked As Support" value={sku?.markedAsSupport} />
                            <FlagLine label="In Production" value={sku?.postProductionStatus?.InProduction} />
                            <FlagLine label="Principal Production" value={sku?.postProductionStatus?.PrincipalProduction} />
                            <FlagLine label="Principal Client QC" value={sku?.postProductionStatus?.PrincipalClientQC} />
                            <FlagLine label="Principal Delivery" value={sku?.postProductionStatus?.PrincipalDelivery} />
                            {sku?.isReorder ? <FlagLine label="Reordered SKU" value={sku?.isReorder} /> : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
